import { SolidStatusPill } from '@odo/shared-ui';
import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useResourcesConfig } from '../config/ResourcesConfigContext';
import { useSearchScope } from '../context/CommandPaletteContext';
import { useResourcePageTitle } from '../hooks/useResourcePageTitle';
import { useResourcesLibrary } from '../providers/resourcesLibraryProvider';
import type { HelpDocumentData } from '../providers/helpCenterProvider';
import { EditResourceButton } from '../authoring/AuthoringButtons';
import InternalBadge from '../components/InternalBadge';
import PinButton from '../components/PinButton';
import ResourceDetailLayout from '../components/ResourceDetailLayout';
import ResourceKindIcon from '../components/ResourceKindIcon';
import ResourcesPageHeader from '../components/ResourcesPageHeader';
import { ResourcePdfViewer } from '../components/ResourcePdfViewer';
import { ResourceEmpty, ResourceError, ResourceLoading } from '../components/ResourceStates';
import { formatResourceDate } from '../utils/resourceIndex';

const SectionDocRow: React.FC<{
    doc: HelpDocumentData;
    isCurrent: boolean;
    basePath: string;
}> = ({ doc, isCurrent, basePath }) => (
    <Link
        to={`${basePath}/help-center/${doc.slug}`}
        aria-current={isCurrent ? 'page' : undefined}
        className={`flex gap-3 items-center p-2 rounded-lg transition-colors ${
            isCurrent ? 'bg-gray-100' : 'hover:bg-gray-50'
        }`}
    >
        <ResourceKindIcon kind="document" />
        <div className="min-w-0 flex flex-col gap-0.5">
            <span className="text-xs font-bold text-gray-800 leading-snug line-clamp-2">
                {doc.title}
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                Published {formatResourceDate(doc.createdAt)}
                <InternalBadge audience={doc.audience} />
            </span>
        </div>
    </Link>
);

/** Help document detail: same two-column shape as VideoDetailPage, PDF card left, section rail right. */
export const HelpDocumentDetailPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const { basePath } = useResourcesConfig();
    const { helpDocuments, isLoading, error, refresh } = useResourcesLibrary();

    const document = useMemo(
        () => helpDocuments.find((item) => item.slug === slug),
        [helpDocuments, slug]
    );

    useResourcePageTitle(document?.title ?? 'Help Center');
    useSearchScope('document');

    // The section rail: documents sharing this one's primary category,
    // alphabetical like the Help Center list; all documents when untagged.
    const sectionDocs = useMemo(() => {
        if (!document) return [];
        const primaryCategory = document.category[0];
        const pool = primaryCategory
            ? helpDocuments.filter((item) => item.category.includes(primaryCategory))
            : helpDocuments;
        return [...pool].sort((a, b) => a.title.localeCompare(b.title));
    }, [helpDocuments, document]);

    if (isLoading && helpDocuments.length === 0) {
        return (
            <div className="flex-1 min-h-0">
                <ResourceLoading label="Loading document…" />
            </div>
        );
    }

    if (error && helpDocuments.length === 0) {
        return (
            <div className="flex-1 min-h-0">
                <ResourceError message={error} onRetry={() => refresh(true)} />
            </div>
        );
    }

    if (!document) {
        return (
            <div className="flex flex-col min-h-0 flex-1">
                <ResourcesPageHeader
                    title="Help Center"
                    searchPlaceholder="Search the Help Center…"
                />
                <ResourceEmpty
                    title="Document not found"
                    description="This document might have been removed, or the link is out of date."
                />
            </div>
        );
    }

    const positionIndex = sectionDocs.findIndex((item) => item.slug === document.slug);

    return (
        <ResourceDetailLayout
            crumbs={[
                { label: 'Resources', to: basePath },
                { label: 'Help Center', to: `${basePath}/help-center` },
                { label: document.title }
            ]}
            searchPlaceholder="Search the Help Center…"
            rail={
                sectionDocs.length > 0
                    ? {
                          title: document.category[0] ?? 'All documents',
                          positionLabel:
                              positionIndex >= 0
                                  ? `${positionIndex + 1} of ${sectionDocs.length}`
                                  : undefined,
                          children: sectionDocs.map((item) => (
                              <SectionDocRow
                                  key={item.id}
                                  doc={item}
                                  isCurrent={item.slug === document.slug}
                                  basePath={basePath}
                              />
                          ))
                      }
                    : null
            }
        >
            <div className="h-full min-h-0 bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col gap-3.5">
                <div className="flex items-center justify-between gap-3 flex-shrink-0">
                    <h1 className="min-w-0 flex-1 text-lg font-bold text-gray-800 leading-snug">
                        {document.title}
                    </h1>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <InternalBadge audience={document.audience} />
                        <EditResourceButton
                            kind="helpDocument"
                            item={document}
                            name={document.title}
                        />
                        <PinButton kind="document" id={document.id} title={document.title} />
                    </div>
                </div>

                <div className="flex items-center justify-between gap-3 flex-wrap flex-shrink-0">
                    <span className="text-xs leading-none text-gray-400">
                        Published {formatResourceDate(document.createdAt)}
                    </span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {document.category.map((cat) => (
                            <SolidStatusPill key={cat} value={cat} />
                        ))}
                    </div>
                </div>

                <div className="flex-1 min-h-0">
                    <ResourcePdfViewer
                        fileId={document.pdfFileId}
                        showControls
                        className="h-full"
                    />
                </div>
            </div>
        </ResourceDetailLayout>
    );
};

export default HelpDocumentDetailPage;
